import { Link } from 'react-router-dom';
import {
  UXP_CS_ID,
  DASHBOARD_METRICS,
  LISTING_FIELDS,
  WRITING_ACTIONS,
  SEO_ANALYSES,
  KEYWORD_TYPES,
  COMPETITOR_DIMENSIONS,
  OPTIMIZATION_TARGETS,
  GOVERNANCE_RULES,
  LOCALIZATION_FEATURES,
  SCORING_DIMENSIONS,
  VERSIONING_FEATURES,
  EXPERIMENT_TYPES,
  PUBLISHING_STAGES,
  COPILOT_CAPABILITIES,
  COLLABORATION_FEATURES,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  ANALYTICS_METRICS,
  LIVE_ROUTES,
  DEMO_DASHBOARD_HEALTH,
  DEMO_CONTENT_SCORES,
  DEMO_SEO_SCORES,
  DEMO_KEYWORDS,
  DEMO_COPILOT_INSIGHTS,
  DEMO_COMPLIANCE,
  DEMO_LISTING,
  buildContentStudioDashboard,
} from '../../contentstudio/framework';
import {
  ContentDashboardPanel,
  ListingBuilderPanel,
  AIWritingStudio,
  SEOEnginePanel,
  KeywordEngineCard,
  ContentScoreGrid,
  ComplianceChecklist,
  ContentCopilotCard,
} from '../../components/contentstudio';
import { Card, KpiCard } from '../../components/ui';

export function ContentStudioPage() {
  const dashboard = buildContentStudioDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{UXP_CS_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">AI Content Studio</h1>
        <p className="mt-1 text-sm text-slate-500">
          Listing intelligence, SEO engine, AI writing studio, brand governance, compliance validation, and content publishing — the Content Operating System.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-ai-200 bg-ai-50 px-3 py-1.5 text-xs font-medium text-ai-700 hover:bg-ai-100 dark:border-ai-900 dark:bg-ai-950 dark:text-ai-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Writing Actions" value={dashboard.writing_actions.toString()} />
        <KpiCard label="SEO Analyses" value={dashboard.seo_analyses.toString()} />
        <KpiCard label="Scoring Dimensions" value={dashboard.scoring_dimensions.toString()} />
        <KpiCard label="Governance Rules" value={dashboard.governance_rules.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Content Dashboard</h2>
        <p className="mt-1 text-sm text-slate-500">{DASHBOARD_METRICS.join(' · ')}</p>
        <div className="mt-4">
          <ContentDashboardPanel metrics={DEMO_DASHBOARD_HEALTH} />
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Listing Builder</h2>
          <Card className="mt-4" padding="sm">
            <ListingBuilderPanel listing={DEMO_LISTING} fields={LISTING_FIELDS} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">AI Writing Studio</h2>
          <Card className="mt-4" padding="sm">
            <AIWritingStudio actions={WRITING_ACTIONS} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">SEO Engine</h2>
        <p className="mt-1 text-sm text-slate-500">{SEO_ANALYSES.join(' · ')}</p>
        <div className="mt-4">
          <SEOEnginePanel scores={DEMO_SEO_SCORES} />
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <KeywordEngineCard keywords={DEMO_KEYWORDS} types={KEYWORD_TYPES} />
        <ContentCopilotCard insights={DEMO_COPILOT_INSIGHTS} />
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Scoring</h2>
        <p className="mt-1 text-sm text-slate-500">{SCORING_DIMENSIONS.join(' · ')}</p>
        <div className="mt-4">
          <ContentScoreGrid scores={DEMO_CONTENT_SCORES} />
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Compliance Validation</h2>
        <Card className="mt-4" padding="sm">
          <ComplianceChecklist items={DEMO_COMPLIANCE} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Brand & Prompt Governance</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {GOVERNANCE_RULES.map((r) => (
              <span key={r} className="rounded-lg border border-brand-200 bg-brand-50 px-2 py-1 text-xs text-brand-700 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-300">{r}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Competitor Analysis</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {COMPETITOR_DIMENSIONS.map((d) => (
              <span key={d} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{d}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Content Optimization</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {OPTIMIZATION_TARGETS.map((t) => (
              <span key={t} className="rounded-lg border border-ai-200 bg-ai-50 px-2 py-1 text-xs text-ai-700 dark:border-ai-800 dark:bg-ai-950 dark:text-ai-300">{t}</span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Localization</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {LOCALIZATION_FEATURES.map((f) => (
              <li key={f}>✓ {f}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Publishing Pipeline</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {PUBLISHING_STAGES.map((s, i) => (
              <span key={s} className="flex items-center gap-1 text-xs">
                <span className="rounded-lg border border-orange-200 bg-orange-50 px-2 py-1 text-orange-700 dark:border-orange-900 dark:bg-orange-950 dark:text-orange-300">{s}</span>
                {i < PUBLISHING_STAGES.length - 1 && <span className="text-slate-400">→</span>}
              </span>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Experimentation</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {EXPERIMENT_TYPES.map((e) => (
              <span key={e} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{e}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Versioning</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {VERSIONING_FEATURES.map((f) => (
              <li key={f}>✓ {f}</li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Collaboration</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {COLLABORATION_FEATURES.map((f) => (
              <li key={f}>✓ {f}</li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Accessibility</h2>
          <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-slate-400">
            {ACCESSIBILITY_STANDARDS.map((s) => (
              <li key={s}>✓ {s}</li>
            ))}
          </ul>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Copilot Capabilities</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {COPILOT_CAPABILITIES.map((c) => (
            <span key={c} className="rounded-lg border border-ai-200 bg-ai-50 px-2 py-1 text-xs text-ai-700 dark:border-ai-800 dark:bg-ai-950 dark:text-ai-300">{c}</span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Analytics Metrics</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ANALYTICS_METRICS.map((m) => (
            <span key={m} className="rounded-lg border border-slate-200 px-2 py-1 text-xs dark:border-slate-700">{m}</span>
          ))}
        </div>
      </section>

      <p className="text-xs text-slate-500">
        Spec: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">docs/uxp/UXP-00005_AI_CONTENT_STUDIO.md</code>
        {' · '}
        Registry: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">pkg/contentstudio/</code>
        {' · '}
        SEO Engine: <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">pkg/aicatalog/seo.go</code>
      </p>
    </div>
  );
}
